import {
  AssessmentSnippet,
  CoverageNextActions,
  ListNextActionEntity,
  PopulatedPregnancyPlans,
} from '../../types/assessments/assessment'
import { ClaimSnippet } from '../../types/claims/claim'
import { PaymentReceived } from '../../types/payments'
import { NextAction, WithId } from '../../types/utils/common'
import { toSearchString } from '../data'
import { getDateString } from '../dates'
import { getCoverageNextActions } from '../insurance'
import { gynNextActionEntity } from './gyn'

export const stringifyNextAction = (
  nextAction: NextAction | null | undefined,
  entity?: ListNextActionEntity | null,
) => {
  if (!nextAction) return ''
  const { text } = nextAction
  const parts: string[] = []
  if (text) parts.push(text)
  const insuranceProviderName =
    entity?.type === 'coverage' ? entity.coverage?.insuranceProvider?.name : null
  if (insuranceProviderName) parts.push(insuranceProviderName)
  return parts.map(p => toSearchString(p)).join('|')
}

const stringifyClaims = (claims: Record<string, ClaimSnippet> | null | undefined) => {
  let claimsString = 'Claims:'
  if (!claims) return claimsString
  Object.values(claims ?? {}).forEach((claim: ClaimSnippet, i) => {
    if (claim.abilitySnippet?.ClaimNo) {
      claimsString += `${claim.abilitySnippet.ClaimNo}${i !== Object.keys(claims).length - 1 ? '|' : ''}`
    }
  })
  return claimsString
}

const stringifyReceivedPayments = (receivedPayments: WithId<PaymentReceived>[] | undefined) => {
  let receivedPaymentsString = 'Payments:'
  if (!receivedPayments) return receivedPaymentsString
  const paymentsWithCheckNumber = receivedPayments.filter(p => !!p.eftCheckNumber)
  paymentsWithCheckNumber.forEach((payment: WithId<PaymentReceived>, i) => {
    receivedPaymentsString += `${payment.eftCheckNumber}${
      i !== paymentsWithCheckNumber.length - 1 ? '|' : ''
    }`
  })
  return receivedPaymentsString
}

const stringifyAuthRefNumbers = (authRefNumbers: string[] | undefined) => {
  let authRefNumbersString = 'Auths:'
  if (!authRefNumbers) return authRefNumbersString
  authRefNumbers
    .filter(a => !!a)
    .forEach((authRefNumber: string, i) => {
      authRefNumbersString += ` ${authRefNumber}${
        i !== Object.keys(authRefNumbers).length - 1 ? '|' : ''
      }`
    })
  return authRefNumbersString
}

const stringifyNextActions = (
  plans: PopulatedPregnancyPlans | undefined,
  nextActions: CoverageNextActions | null | undefined,
  gynNextAction: NextAction | null | undefined,
) => {
  const nextActionsArray = getCoverageNextActions(plans, nextActions)
  let nextActionsString = 'Next Actions:'
  if (!nextActionsArray) return nextActionsString
  const nextActionsWithText = nextActionsArray.filter(
    ({ nextAction }) => !!nextAction && !!nextAction.text,
  )
  nextActionsWithText.forEach((nextActionData, i) => {
    const { nextAction, coverage, id } = nextActionData
    if (nextAction?.text) {
      nextActionsString += `${stringifyNextAction(nextAction, { coverage, id, type: 'coverage' })}${i !== nextActionsWithText.length - 1 ? '|' : ''}`
    }
  })
  if (gynNextAction?.text) {
    nextActionsString += stringifyNextAction(gynNextAction, gynNextActionEntity)
  }
  return nextActionsString
}

export const stringifySnippet = (
  snippet: Partial<AssessmentSnippet>,
  // plans: PopulatedInsurancePlans,
) => {
  const {
    phone = 'zzz',
    email = 'zzz',
    fname = 'zzz',
    lname = 'zzz',
    // nextActionText = 'zzz',
    midwifeName = 'zzz',
    insuranceCoverage = 'zzz',
    partnerEmail,
    assessmentName = 'zzz',
    // stages = [],
    nickname,
    partnerName,

    deliveredOn,
    authRefNumbers,
    // additionalAuthRefNumbers,
    receivedPayments,
    // additionalClaimsRefNumbers,
    claims,
    claimRefNumbers,
    // patientPlans,
    // patientNextActions,
    gynNextAction,
    plans,
    nextActions,
    dob,
    hasOverduePayment,
  } = snippet ?? {}

  const searchable: string[] = [
    `${fname}${nickname ? ` ${nickname}` : ''} ${lname}`,
    midwifeName,
    insuranceCoverage,
    partnerName ?? '',
    partnerEmail ?? '',
    assessmentName,
    stringifyReceivedPayments(receivedPayments),
    stringifyClaims(claims),
    stringifyAuthRefNumbers(authRefNumbers),
    stringifyNextActions(plans, nextActions, gynNextAction),
    deliveredOn ? getDateString(deliveredOn, 'short') : '',
    authRefNumbers?.join('|') ?? '',
    claimRefNumbers?.join('|') ?? '',
    email ?? '',
    phone,
    dob ? getDateString(dob, 'short') : '',
    hasOverduePayment ? 'overdue payment' : '',
    // nextActionText,
    // stages.join('|'),
    // status,
  ]

  return searchable
    .filter(s => !!s && s !== 'zzz')
    .map(s => toSearchString(s ?? ''))
    .join(' ')
}
